import { supabase } from '@/app/lib/supabase';
import type { NotificationData } from '@/app/types/interfaces';

type ShellProfile = {
  profile_picture_url?: string | null;
  display_name?: string | null;
};

export async function getShellProfile(userId: string): Promise<ShellProfile | null> {
  const { data, error } = await supabase.functions.invoke('users', {
    body: { action: 'get', userId },
  });
  const profile = !error && Array.isArray(data) ? (data[0] as ShellProfile | undefined) ?? null : null;
  if (profile?.profile_picture_url) return profile;
  // Presentation only: provider metadata never grants access or authorizes actions.
  const { data: sessionData } = await supabase.auth.getSession();
  const authUser = sessionData.session?.user;
  if (authUser?.id !== userId) {
    if (error) throw error;
    return profile;
  }
  const metadata = authUser.user_metadata;
  const picture = [metadata?.avatar_url, metadata?.picture].find(value => typeof value === 'string' && value.trim());
  const name = [metadata?.full_name, metadata?.name].find(value => typeof value === 'string' && value.trim());
  return { ...profile, profile_picture_url: picture ?? null, display_name: profile?.display_name || name || null };
}

let notificationCache:
  | { userId: string; fetchedAt: number; value: NotificationData[] }
  | null = null;

function invalidateNotifications() {
  notificationCache = null;
}

export const shellNotificationsApi = {
  async getAll(userId: string): Promise<NotificationData[]> {
    if (
      notificationCache?.userId === userId
      && Date.now() - notificationCache.fetchedAt < 30_000
    ) {
      return notificationCache.value;
    }
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    const value = (data ?? []) as NotificationData[];
    notificationCache = { userId, fetchedAt: Date.now(), value };
    return value;
  },

  async markAsRead(notificationId: number) {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId);
    if (error) throw error;
    invalidateNotifications();
  },

  async markAllAsRead(userId: string) {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false);
    if (error) throw error;
    invalidateNotifications();
  },

  async delete(notificationId: number) {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);
    if (error) throw error;
    invalidateNotifications();
  },
};
